/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import React, { forwardRef, useEffect } from 'react'
import { useLang } from '~/hooks/useLang'
import { DataGrid, GridColDef, gridClasses, GridSlots, GridRow, type GridRowProps } from '@mui/x-data-grid'
import {
  Avatar,
  Box,
  Button,
  Chip,
  LinearProgress,
  keyframes,
  Tooltip,
} from '@mui/material'
import Brightness1Icon from '@mui/icons-material/Brightness1'
import { elapsedTime } from '~/utils/dateTime'
import useSystemState from '~/features/System/stores/SystemState'
import useDispatchTableState from '~/features/Dispatch/components/TabListDispatch/stores/DispatchTableState'
import useDispatchState from '~/features/Dispatch/stores/DispatchState'
import { Star } from '@mui/icons-material'
import { State, useHookstate, type StateMethods } from '@hookstate/core'
import { useTheme } from '~/hooks/useTheme'
import { ptBR } from '@mui/x-data-grid/locales'
import NoRowsOverlay from '~/components/NoRowsOverlay'
import IListDispatchSocket from '~/components/ListDispatchesSocket/interfaces/IListDispatchesProps'
import dayjs from 'dayjs'
import { useDroppable, useDraggable } from '@dnd-kit/core';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

interface IDispatchTableUpdateSocket {
  readonly typeStatus: string
  readonly loadingRows: State<boolean>
  readonly fullList?: boolean
  readonly enableCheckbox?: boolean
  readonly searchCity: StateMethods<string>
  readonly rowsSelected?: State<readonly IListDispatchSocket[]>
}

export default function DispatchTableUpdateSocket({
  typeStatus,
  loadingRows,
  fullList = false,
  enableCheckbox = false,
  rowsSelected,
  searchCity,
}: IDispatchTableUpdateSocket) {
  const { darkMode } = useTheme()
  const { translate } = useLang()
  const { getStatusColors, getPriorityColors } = useSystemState()
  const {
    dispatchRowsTable,
    updateOccurrenceRowsTable,
    dispatchRowsTableFiltered,
    triggerDispatchNewSocketState,
  } = useDispatchTableState()
  const { syncDispatchFromId } = useDispatchState()
  const loading = useHookstate(false)

  useEffect(() => {
    const cityLowerCase = searchCity.get().toLocaleLowerCase()

    if (searchCity.get().length > 3) {
      const allDispatchs = dispatchRowsTable()
        .get()
      ['dispatched'].concat(dispatchRowsTable().get()['done'])
        .concat(dispatchRowsTable().get()['on-hold'])
        .concat(dispatchRowsTable().get()['pending'])

      const dispatchsFiltereds = JSON.parse(
        JSON.stringify(
          allDispatchs.filter((dispatch) =>
            dispatch.city.toLocaleLowerCase().includes(cityLowerCase)
          )
        )
      )
      dispatchRowsTableFiltered().set({
        pending: JSON.parse(
          JSON.stringify(
            dispatchsFiltereds.filter((row) => row['statusId'] == 1)
          )
        ),
        dispatched: JSON.parse(
          JSON.stringify(
            dispatchsFiltereds.filter(
              (row) => row['statusId'] > 1 && row['statusId'] < 8
            )
          )
        ),
        done: JSON.parse(
          JSON.stringify(
            dispatchsFiltereds.filter((row) => row['statusId'] == 8)
          )
        ),
        'on-hold': JSON.parse(
          JSON.stringify(
            dispatchsFiltereds.filter((row) => row['statusId'] == 9)
          )
        ),
      })
    } else {
      const allDispatchs = dispatchRowsTable()
        .get()
      ['dispatched'].concat(dispatchRowsTable().get()['done'])
        .concat(dispatchRowsTable().get()['on-hold'])
        .concat(dispatchRowsTable().get()['pending'])

      dispatchRowsTableFiltered().set({
        pending: JSON.parse(
          JSON.stringify(allDispatchs.filter((row) => row['statusId'] == 1))
        ),
        dispatched: JSON.parse(
          JSON.stringify(
            allDispatchs.filter(
              (row) => row['statusId'] > 1 && row['statusId'] < 8
            )
          )
        ),
        done: JSON.parse(
          JSON.stringify(allDispatchs.filter((row) => row['statusId'] == 8))
        ),
        'on-hold': JSON.parse(
          JSON.stringify(allDispatchs.filter((row) => row['statusId'] == 9))
        ),
      })
    }
  }, [
    searchCity.get(),
    loadingRows.get(),
    triggerDispatchNewSocketState().get(),
  ])

  const handleClickAction = (params) => {
    const paramDispatchId: number | null = params.row.id ? params.row.id : null
    loading.set(true)
    syncDispatchFromId(paramDispatchId).finally(() => {
      loading.set(false)
      try {
        updateOccurrenceRowsTable({
          occurrenceId: params.row.occurrence_id,
          recallWasViewed: true,
        })
      } catch (error) {
        console.error(error)
      }
    })
  }

  const onRowsSelectionHandler = (ids) => {
    if (enableCheckbox && rowsSelected) {
      const selectedRowsData = ids.map((id) =>
        JSON.parse(JSON.stringify(dispatchRowsTable().get()[typeStatus])).find(
          (row) => row.id === id
        )
      )
      rowsSelected ? rowsSelected?.set(selectedRowsData) : false
    }
  }

  function DraggableCodeCell({ row, onClick }) {
    const {
      attributes,
      listeners,
      setNodeRef: setDragRef,
      transform,
      isDragging
    } = useDraggable({
      id: `dispatch-${row.id}`,
      data: { dispatch: row },
    });

    const { setNodeRef: setDropRef, isOver } = useDroppable({
      id: `dispatch-drop-${row.id}`,
      data: { dispatch: row },
    });

    const combinedRef = (node: HTMLDivElement | null) => {
      setDragRef(node);
      setDropRef(node);
    };

    const style: React.CSSProperties = {
      transform: transform
        ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
        : undefined,
      opacity: isDragging ? 0.5 : 1,
      cursor: isDragging ? 'grabbing' : 'grab',
      zIndex: isDragging ? 1000 : 'auto',
      position: isDragging ? 'relative' : 'static',
      padding: isOver ? '4px' : '0',
      borderRadius: isOver ? '4px' : '0',
      transition: 'background-color 0.2s ease, padding 0.2s ease',
    };

    return (
      <div
        ref={combinedRef}
        style={style}
        {...listeners}
        {...attributes}
      >
        <Button
          startIcon={<DragIndicatorIcon />}
          type="submit"
          variant="contained"
          onClick={(event) => {
            if (!isDragging) {
              event.stopPropagation();
              onClick();
            }
          }}
          sx={{
            backgroundColor: getStatusColors(row.statusId).main,
            color: getStatusColors(row.statusId).contrastText,
            '&:hover': {
              backgroundColor: getStatusColors(row.statusId).dark,
              color: getStatusColors(row.statusId).contrastText,
            },
          }}
        >
          {row.code}
        </Button>
      </div>
    );
  }

  const columns: readonly GridColDef[] = [
    { field: 'id' },
    { field: 'occurrence_id' },
    { field: 'call' },
    { field: 'recall_was_viewed', type: 'boolean' },
    { field: 'closure_requested' },
    {
      field: 'code',
      headerName: translate('Nº Occurrence'),
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        return (
        typeStatus === 'pending' && !fullList ? (
        <DraggableCodeCell
          row={params.row}
          onClick={() => handleClickAction(params)}
            />) : (
          <Tooltip
            title={`Despachado em: ${dayjs(params.row.dispatched_at).format(
              'DD/MM/YYYY HH:mm'
            )}`}
            arrow
          >
              <Button
                type="submit"
                variant="contained"
                onClick={() => handleClickAction(params)}
                sx={{
                  backgroundColor: getStatusColors(params.row.statusId).main,
                  color: getStatusColors(params.row.statusId).contrastText,
                  '&:hover': {
                    backgroundColor: getStatusColors(params.row.statusId).dark,
                    color: getStatusColors(params.row.statusId).contrastText,
                  },
                }}
              >
                {params.value}
              </Button>
          </Tooltip>
        )
      )},
    },
    {
      field: 'status',
      headerName: translate('Status'),
      flex: 0.4,
      sortable: true,
    },
    { field: 'status_updated_at' },
    {
      field: 'dispatch_group',
      headerName: translate('Dispatch Group'),
      flex: 1,
      sortable: true,
    },
    { field: 'type', headerName: translate('Type'), flex: 1.5, sortable: true },
    {
      field: 'subtype',
      headerName: translate('Subtype'),
      flex: 1.5,
      sortable: true,
    },
    {
      field: 'location',
      headerName: translate('Location'),
      flex: 1.5,
      sortable: true,
    },
    {
      field: 'city',
      headerName: translate('City'),
      flex: 0.5,
      sortable: true,
      renderCell: (params) => {
        return (
          params.row.city +
          (params.row.district ? ` - ${params.row.district}` : '')
        )
      },
    },
    { field: 'notes', headerName: translate('Comment'), flex: 1.5 },
    {
      field: 'priority',
      headerName: translate('Priority'),
      flex: 0.4,
      sortable: true,
      renderCell: (params) => {
        return (
          <Chip
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            avatar={
              <Avatar>
                <Brightness1Icon
                  sx={{ color: getPriorityColors(params.row.priority).main }}
                />
              </Avatar>
            }
            label={params.row.priority}
          />
        )
      },
    },
    {
      field: 'elapsed_time',
      headerName: translate('Elapsed Time'),
      flex: 0.4,
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      renderCell: (params) => {
        const initialDate = dayjs(params.row.elapsed_time)
        const currentDate = dayjs()
        const timeInMinutes = currentDate.diff(initialDate, 'minute') // 7

        let bgcolor
        let textColor

        if (darkMode) {
          if (timeInMinutes > 5) {
            bgcolor = '#F51800'
            textColor = '#FFFFFF'
          } else if (timeInMinutes > 3) {
            bgcolor = '#f1c40f'
            textColor = '#000000'
          } else {
            textColor = '#000000'
            bgcolor = '#E0E0E0'
          }
        } else {
          if (timeInMinutes > 5) {
            bgcolor = '#F51800'
            textColor = '#FFFFFF'
          } else if (timeInMinutes > 3) {
            bgcolor = '#f1c40f'
            textColor = '#000000'
          } else {
            textColor = '#000000'
            bgcolor = '#E0E0E0'
          }
        }

        if (typeStatus == 'dispatched' || typeStatus == 'done') {
          return (
            <Tooltip
              title={dayjs(params.row.elapsed_time).format(
                'DD/MM/YYYY HH:mm:ss'
              )}
              arrow
            >
              <span>{elapsedTime(params.row.elapsed_time)}</span>
            </Tooltip>
          )
        }

        return (
          <Tooltip
            arrow
            title={`Atualizado em: ${dayjs(params.row.elapsed_time).format(
              'DD/MM/YYYY HH:mm:ss'
            )}`}
          >
            <Chip
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: bgcolor,
                color: textColor,
              }}
              label={elapsedTime(params.row.elapsed_time)}
              // ref={refToNewComment}
            />
          </Tooltip>
        )
      },
    },
    {
      field: 'highlight',
      headerName: translate('Highlight'),
      flex: 0.3,
      align: 'center',
      sortable: true,
      renderCell: (params) => {
        return params.row.highlight ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            height="100%"
          >
            {params.row.highlight ? <Star color="warning" /> : null}
          </Box>
        ) : null
      },
    },
  ]
  const flashes = keyframes`
    0% { background: ${darkMode ? '#121212' : '#FFF'}}
    100% {background: #ffa726}
  `

  const flashesBK = keyframes`
    0% { background: ${darkMode ? '#121212' : '#FFF'}}
    100% {background: #BFBFBF}
  `

  const DroppableRow = forwardRef<HTMLDivElement, GridRowProps>(
    (props, ref) => {
      const { row, ...otherProps } = props;

      const { setNodeRef, isOver } = useDroppable({
        id: `dispatch-${row.id}`,
        data: { dispatch: row },
      });

      const style: React.CSSProperties = {
        backgroundColor: isOver
          ? 'rgba(25, 118, 210, 0.15)'
          : 'transparent',
        transition: 'background-color 0.2s ease',
        borderLeft: isOver ? '4px solid #1976d2' : 'none',
      };

      return (
        <div ref={setNodeRef} style={style}>
          <GridRow
            ref={ref}
            row={row}
            {...otherProps}
          />
        </div>
      );
    }
  );

  DroppableRow.displayName = 'DroppableRow';

  return (
    <DataGrid
      sx={{
        height:
          JSON.parse(
            JSON.stringify(dispatchRowsTableFiltered().get()[typeStatus])
          ).length > 0
            ? 'auto'
            : 300,
        [`.${gridClasses.columnHeaderTitle}`]: {
          fontWeight: 'bold !important',
        },
        cursor: 'pointer',
        '& .flashesRows': {
          background: '#ffa726',
          border: '1px solid #fff',
          animationIterationCount: '4',
          animationTimingFunction: 'ease',
          animationDuration: '2.5s',
          animationName: `${flashes}`,
        },
        '& .closureRequestedFlashesRows': {
          background: '#BFBFBF',
          border: '1px solid #fff',
          animationIterationCount: '4',
          animationTimingFunction: 'ease',
          animationDuration: '2.5s',
          animationName: `${flashesBK}`,
        },
      }}
      loading={loading.get() || loadingRows ? loadingRows.get() : false}
      slots={{
        loadingOverlay: LinearProgress as GridSlots['loadingOverlay'],
        noRowsOverlay: NoRowsOverlay,
      }}
      getRowClassName={({ row }) => {
        return typeStatus != 'done'
          ? row.call > 1 && !row.recall_was_viewed
            ? 'flashesRows'
            : row.closure_requested
              ? 'closureRequestedFlashesRows'
              : ''
          : ''
      }}
      localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
      columns={columns}
      rows={JSON.parse(
        JSON.stringify(dispatchRowsTableFiltered().get()[typeStatus])
      )}
      paginationMode={'client'}
      initialState={{
        pagination: { paginationModel: { pageSize: fullList ? 60 : 5 } },
        sorting: {
          sortModel: [{ field: 'elapsed_time', sort: 'desc' }],
        },
      }}
      columnVisibilityModel={{
        id: false,
        occurrence_id: false,
        closure_requested: false,
        call: false,
        recall_was_viewed: false,
        notes: false,
        status_updated_at: false,
      }}
      getRowId={(row) => Number(row.id)}
      checkboxSelection={enableCheckbox}
      onRowSelectionModelChange={(ids) => {
        onRowsSelectionHandler(ids)
      }}
      disableColumnSelector
      disableRowSelectionOnClick
      disableColumnMenu
    />
  )
}
